import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios, { authHeaders } from "../../utils/axios";

export const fetchPostComments = createAsyncThunk(
  "postComments/fetchPostComments",
  async ({ postId, page = 1, limit = 10, search = "" }, { rejectWithValue }) => {
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_URL}/post-comments?post_id=${postId}&page=${page}&limit=${limit}${searchParam}`,
        authHeaders()
      );
      return { data: response.data.data, meta: response.data.meta, page };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const updatePostComment = createAsyncThunk(
  "postComments/updatePostComment",
  async ({ commentId, content }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(
        `${process.env.REACT_APP_BASE_URL}/post-comments/${commentId}`,
        { content },
        authHeaders()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const deletePostComment = createAsyncThunk(
  "postComments/deletePostComment",
  async ({ commentId }, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(
        `${process.env.REACT_APP_BASE_URL}/post-comments/${commentId}`,
        authHeaders()
      );
      return { commentId, ...response.data };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

const postCommentSlice = createSlice({
  name: "postComments",
  initialState: {
    comments: [],
    meta: null,
    commentsLoading: false,
    updateCommentLoading: false,
    deleteCommentLoading: false,
    error: null,
  },
  reducers: {
    clearComments: (state) => {
      state.comments = [];
      state.meta = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchPostComments
      .addCase(fetchPostComments.pending, (state, action) => {
        state.commentsLoading = true;
        // Don't clear comments here to prevent UI flicker during search/refresh.
      })
      .addCase(fetchPostComments.fulfilled, (state, action) => {
        state.commentsLoading = false;
        const { data, meta, page } = action.payload;
        if (page === 1) {
          state.comments = data;
        } else {
          state.comments = [...state.comments, ...data];
        }
        state.meta = meta;
      })
      .addCase(fetchPostComments.rejected, (state, action) => {
        state.commentsLoading = false;
        state.error = action.payload;
      })
      // updatePostComment
      .addCase(updatePostComment.pending, (state) => {
        state.updateCommentLoading = true;
      })
      .addCase(updatePostComment.fulfilled, (state, action) => {
        state.updateCommentLoading = false;
        const updatedComment = action.payload.data;
        const index = state.comments.findIndex((c) => c.id === updatedComment.id);
        if (index !== -1) {
          // Only update safe fields — the update API doesn't return nested user/company
          state.comments[index].content = updatedComment.content;
          state.comments[index].updated_at = updatedComment.updated_at;
        }
      })
      .addCase(updatePostComment.rejected, (state, action) => {
        state.updateCommentLoading = false;
        state.error = action.payload;
      })
      // deletePostComment
      .addCase(deletePostComment.pending, (state) => {
        state.deleteCommentLoading = true;
      })
      .addCase(deletePostComment.fulfilled, (state, action) => {
        state.deleteCommentLoading = false;
        state.comments = state.comments.filter(
          (c) => c.id !== action.payload.commentId
        );
      })
      .addCase(deletePostComment.rejected, (state, action) => {
        state.deleteCommentLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearComments } = postCommentSlice.actions;
export default postCommentSlice.reducer;
